import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Unit } from "@/types/database";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

export function useUnits() {
  const { activeOrgId } = useAuth();

  return useQuery({
    queryKey: ["units", activeOrgId],
    enabled: !!activeOrgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("units")
        .select("*")
        .eq("organization_id", activeOrgId!)
        .order("unit_number");
      if (error) throw error;
      return data as Unit[];
    },
  });
}

export function useCreateUnit() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async (unit: {
      unit_number: string;
      owner_name: string;
      owner_email?: string;
      owner_phone?: string;
      str_approved?: boolean;
      status?: string;
      strata_hotel_category?: string;
      floor_number?: number;
      bedrooms?: number;
      bathrooms?: number;
      sq_footage?: number;
      parking_stall?: string;
      locker_number?: string;
      notes?: string;
    }) => {
      if (!activeOrgId) throw new Error("No active organization");
      const { data, error } = await supabase
        .from("units")
        .insert({ ...unit, organization_id: activeOrgId })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["units", activeOrgId] });
      toast({ title: "Unit created", description: "The unit has been added successfully." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useUpdateUnit() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Unit> & { id: string }) => {
      const { data, error } = await supabase
        .from("units")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["units", activeOrgId] });
      toast({ title: "Unit updated", description: "The unit has been updated successfully." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useDeleteUnit() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("units").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["units", activeOrgId] });
      toast({ title: "Unit deleted", description: "The unit has been removed." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}
